import React, { useEffect, useState } from "react";
import { Badge, Button, Form, ListGroup, Modal, Spinner, Table } from "react-bootstrap";
import { toast } from "react-toastify";
import { linkUnlinkedNumber, markNotCustomer, searchLeadsForCallLink, unmarkNotCustomer } from "../../services/leadTrackerApi";
import { formatDateTimeDMY12 } from "../../utils/dateFormat";

// Unlinked calls: calls on an employee's extension with numbers no lead has. They count in the
// employee's Actual Call Time from the start; linking only decides which lead they belong to.

const NON_CUSTOMER_CATEGORIES = [["HOTEL", "Hotel"], ["DRIVER", "Driver"], ["VENDOR", "Vendor"], ["OFFICE", "Office"], ["OTHER", "Other"]];

export function formatCallSeconds(totalSeconds) {
  const seconds = Math.max(0, Number(totalSeconds || 0));
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${seconds % 60}s`;
  return `${seconds}s`;
}

export function displayNumber(number) {
  const digits = String(number || "");
  return /^\d{10}$/.test(digits) ? `${digits.slice(0, 5)} ${digits.slice(5)}` : digits;
}

function callKind(call) {
  if (call.callDirection !== "INBOUND") return "Dialled";
  return String(call.callStatus || "").trim().toLowerCase() === "answered" ? "Incoming" : "Missed incoming";
}

function categoryLabel(category) {
  return (NON_CUSTOMER_CATEGORIES.find(([value]) => value === category) || [category, category || "-"])[1];
}

const groupKey = (group) => `${group.memberId || ""}-${group.customerNumber}`;

export const NO_EXTENSION_MESSAGE = "No extension is allocated to you, so you cannot use this feature.";

/** Who was on the extension for a call: their name, with the extension number on hover. */
export function CallerName({ call, className = "fw-semibold" }) {
  const extension = call?.extension ? `Ext ${call.extension}` : "";
  if (!call?.calledByName) return <span className={className}>{extension || "-"}</span>;
  return <span className={className} title={extension || undefined}>{call.calledByName}</span>;
}

export function UnlinkedCallsPanel({ data, loading = false, admin = false, token = "", callingNumber = "", onCall, onCreateLead, onChanged }) {
  const [expanded, setExpanded] = useState("");
  const [linking, setLinking] = useState(null);
  const [marking, setMarking] = useState(null);
  const [showHidden, setShowHidden] = useState(false);
  const [undoing, setUndoing] = useState("");
  const open = data?.open || [];
  const hidden = data?.notCustomers || [];

  const undo = async (group) => {
    setUndoing(group.customerNumber);
    try {
      await unmarkNotCustomer(token, group.customerNumber);
      toast.success(`${displayNumber(group.customerNumber)} is back in Unlinked Calls`);
      onChanged?.();
    } catch (error) {
      toast.error(error?.response?.data?.detail || error?.response?.data?.message || "Could not undo");
    } finally {
      setUndoing("");
    }
  };

  const callsRow = (group, columns) => expanded === groupKey(group) && <tr key={`${groupKey(group)}-calls`}>
    <td colSpan={columns} className="bg-light">
      <Table size="sm" className="mb-0">
        <tbody>{group.calls.map((call) => <tr key={call.id}>
          <td className="text-nowrap">{formatDateTimeDMY12(call.calledAt)}</td>
          <td className={callKind(call) === "Missed incoming" ? "text-danger" : ""}>{callKind(call)}{call.callDirection !== "INBOUND" && call.callStatus ? ` - ${call.callStatus}` : ""}</td>
          <td className="text-nowrap"><CallerName call={{ calledByName: group.memberName, extension: call.extension }} className="" /></td>
          <td className="text-end text-nowrap">{formatCallSeconds(call.countedSeconds)}</td>
          <td style={{ minWidth: 220 }}>{call.callRecPath ? <audio controls preload="none" src={call.callRecPath} style={{ width: "100%", height: 32 }} /> : <span className="small text-muted">No recording</span>}</td>
        </tr>)}</tbody>
      </Table>
    </td>
  </tr>;

  const toggle = (group) => setExpanded((current) => (current === groupKey(group) ? "" : groupKey(group)));

  return <div>
    <div className="small text-muted mb-2">
      {admin
        ? "Extension calls with numbers no lead has. They count in each employee's Actual Call Time."
        : "Calls on your extension with numbers no lead has. They already count in your Actual Call Time. Add each number to its lead, create a lead, or mark it as not a customer."}
    </div>
    {loading && <div className="small text-muted mb-2"><Spinner size="sm" className="me-2" />Loading unlinked calls...</div>}
    {!loading && !open.length && <div className="text-muted text-center py-3">No unlinked calls{admin ? " in this period" : ""}.</div>}
    {open.length > 0 && <Table size="sm" hover responsive className="align-middle">
      <thead><tr><th>Number</th>{admin ? <th>Employee</th> : null}<th>Calls</th><th>Last call</th><th className="text-end">Call time</th>{!admin && <th className="text-end">Actions</th>}</tr></thead>
      <tbody>{open.flatMap((group) => [
        <tr key={groupKey(group)} className={group.callBack ? "table-danger" : ""}>
          <td className="text-nowrap">
            <Button variant="link" size="sm" className="p-0 me-2 text-decoration-none" onClick={() => toggle(group)} title="Show the calls">
              <i className={`bi bi-chevron-${expanded === groupKey(group) ? "down" : "right"}`} />
            </Button>
            <span className="fw-semibold">{displayNumber(group.customerNumber)}</span>
            {group.callBack && <Badge bg="danger" className="ms-2">Call back</Badge>}
          </td>
          {admin && <td>{group.memberName || <span className="text-muted">No employee on the extension</span>}</td>}
          <td>{group.callCount}</td>
          <td className="text-nowrap">{formatDateTimeDMY12(group.lastCallAt)}</td>
          <td className="text-end text-nowrap">{formatCallSeconds(group.countedSeconds)}</td>
          {!admin && <td className="text-end">
            <div className="d-flex flex-wrap justify-content-end gap-1">
              {onCall && <Button size="sm" variant={group.callBack ? "success" : "outline-success"} title="Call this number" onClick={() => onCall(group.customerNumber)} disabled={Boolean(callingNumber)}>
                {callingNumber === group.customerNumber ? <Spinner size="sm" /> : <i className="bi bi-telephone-fill" />}
              </Button>}
              <Button size="sm" variant="outline-primary" onClick={() => setLinking(group)}>Add to lead</Button>
              {onCreateLead && <Button size="sm" variant="outline-primary" onClick={() => onCreateLead(group.customerNumber)}>Create lead</Button>}
              <Button size="sm" variant="outline-secondary" onClick={() => setMarking(group)}>Not a customer</Button>
            </div>
          </td>}
        </tr>,
        callsRow(group, 5),
      ])}</tbody>
    </Table>}
    {hidden.length > 0 && <div className="mt-2">
      <Button variant="link" size="sm" className="p-0" onClick={() => setShowHidden((value) => !value)}>
        {showHidden ? "Hide" : "Show"} numbers marked not a customer ({hidden.length})
      </Button>
      {showHidden && <Table size="sm" hover responsive className="align-middle mt-2">
        <thead><tr><th>Number</th>{admin ? <th>Employee</th> : null}<th>Marked as</th><th>Calls</th><th>Last call</th><th className="text-end">Call time</th>{!admin && <th />}</tr></thead>
        <tbody>{hidden.flatMap((group) => [
          <tr key={groupKey(group)}>
            <td className="text-nowrap">
              <Button variant="link" size="sm" className="p-0 me-2 text-decoration-none" onClick={() => toggle(group)} title="Show the calls">
                <i className={`bi bi-chevron-${expanded === groupKey(group) ? "down" : "right"}`} />
              </Button>
              {displayNumber(group.customerNumber)}
            </td>
            {admin && <td>{group.memberName || "-"}</td>}
            <td>{categoryLabel(group.nonCustomerCategory)}{group.nonCustomerNote ? ` - ${group.nonCustomerNote}` : ""}<div className="small text-muted">by {group.markedBy || "-"}</div></td>
            <td>{group.callCount}</td>
            <td className="text-nowrap">{formatDateTimeDMY12(group.lastCallAt)}</td>
            <td className="text-end text-nowrap">{formatCallSeconds(group.countedSeconds)}</td>
            {!admin && <td className="text-end"><Button size="sm" variant="outline-secondary" onClick={() => undo(group)} disabled={undoing === group.customerNumber}>{undoing === group.customerNumber ? <Spinner size="sm" /> : "Undo"}</Button></td>}
          </tr>,
          callsRow(group, 6),
        ])}</tbody>
      </Table>}
    </div>}
    {!admin && <LinkToLeadModal group={linking} token={token} onHide={() => setLinking(null)} onLinked={() => { setLinking(null); onChanged?.(); }} />}
    {!admin && <NotCustomerModal group={marking} token={token} onHide={() => setMarking(null)} onSaved={() => { setMarking(null); onChanged?.(); }} />}
  </div>;
}

function LinkToLeadModal({ group, token, onHide, onLinked }) {
  const [query, setQuery] = useState("");
  const [options, setOptions] = useState([]);
  const [searching, setSearching] = useState(false);
  const [savingId, setSavingId] = useState(null);

  useEffect(() => {
    setQuery("");
    setOptions([]);
  }, [group]);

  useEffect(() => {
    const text = query.trim();
    if (!group || text.length < 3) {
      setOptions([]);
      return undefined;
    }
    let cancelled = false;
    const timer = window.setTimeout(async () => {
      setSearching(true);
      try {
        const rows = await searchLeadsForCallLink(token, text);
        if (!cancelled) setOptions(rows || []);
      } catch {
        if (!cancelled) setOptions([]);
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [query, group, token]);

  const link = async (option, replaceAlternate = false) => {
    setSavingId(option.id);
    try {
      const result = await linkUnlinkedNumber(token, group.customerNumber, option.id, replaceAlternate);
      const moved = result.linkedCalls ? ` - ${result.linkedCalls} call${result.linkedCalls === 1 ? "" : "s"} moved to the lead` : "";
      toast.success(`${displayNumber(group.customerNumber)} added to ${result.leadCode}${moved}`);
      onLinked();
    } catch (error) {
      const message = error?.response?.data?.detail || error?.response?.data?.message;
      // The lead already has a different alternate number: only replaced when the employee says so.
      if (error?.response?.status === 409 && !replaceAlternate) {
        if (window.confirm(message || "This lead already has an alternate number. Replace it?")) await link(option, true);
      } else {
        toast.error(message || "Could not add the number to the lead");
      }
    } finally {
      setSavingId(null);
    }
  };

  return <Modal show={Boolean(group)} onHide={() => { if (!savingId) onHide(); }} centered>
    <Modal.Header closeButton={!savingId}><Modal.Title>Add {displayNumber(group?.customerNumber)} to a lead</Modal.Title></Modal.Header>
    <Modal.Body>
      <div className="small text-muted mb-2">It becomes the lead's alternate number, and its calls move onto the lead. Future calls on it link to the lead by themselves.</div>
      <Form.Control autoFocus placeholder="Lead ID or customer number" value={query} onChange={(event) => setQuery(event.target.value)} disabled={Boolean(savingId)} />
      {searching && <div className="small text-muted mt-2"><Spinner size="sm" className="me-2" />Searching...</div>}
      {!searching && query.trim().length >= 3 && !options.length && <div className="small text-muted mt-2">No matching lead.</div>}
      {options.length > 0 && <ListGroup className="mt-2">{options.map((option) => <ListGroup.Item key={option.id} className="d-flex justify-content-between align-items-center gap-2">
        <div>
          <div className="fw-semibold">{option.leadId} <span className="fw-normal">{option.customerNumber}</span></div>
          <div className="small text-muted">{[option.destinationName, option.interestStatus, option.assignedTeam].filter(Boolean).join(" - ")}</div>
          {option.alternateNumber && <div className="small text-muted">Alternate: {option.alternateNumber}</div>}
        </div>
        <Button size="sm" onClick={() => link(option)} disabled={Boolean(savingId)}>{savingId === option.id ? <Spinner size="sm" /> : "Add"}</Button>
      </ListGroup.Item>)}</ListGroup>}
    </Modal.Body>
  </Modal>;
}

function NotCustomerModal({ group, token, onHide, onSaved }) {
  const [category, setCategory] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setCategory("");
    setNote("");
  }, [group]);

  const save = async (event) => {
    event.preventDefault();
    if (!category) return;
    setSaving(true);
    try {
      await markNotCustomer(token, group.customerNumber, category, note);
      toast.success(`${displayNumber(group.customerNumber)} marked as ${categoryLabel(category).toLowerCase()}`);
      onSaved();
    } catch (error) {
      toast.error(error?.response?.data?.detail || error?.response?.data?.message || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  return <Modal show={Boolean(group)} onHide={() => { if (!saving) onHide(); }} centered>
    <Form onSubmit={save}>
      <Modal.Header closeButton={!saving}><Modal.Title>{displayNumber(group?.customerNumber)} is not a customer</Modal.Title></Modal.Header>
      <Modal.Body>
        <div className="small text-muted mb-2">Its calls leave Unlinked Calls for everyone, and still count in Actual Call Time.</div>
        <Form.Label>Who is it?</Form.Label>
        <div className="d-flex flex-wrap gap-3 mb-3">{NON_CUSTOMER_CATEGORIES.map(([value, label]) => <Form.Check key={value} type="radio" id={`non-customer-${value}`} name="non-customer-category" label={label} checked={category === value} onChange={() => setCategory(value)} disabled={saving} />)}</div>
        <Form.Label>Note (optional)</Form.Label>
        <Form.Control value={note} maxLength={255} placeholder="e.g. hotel name, driver name" onChange={(event) => setNote(event.target.value)} disabled={saving} />
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={saving}>Cancel</Button>
        <Button type="submit" disabled={!category || saving}>{saving ? <Spinner size="sm" /> : "Save"}</Button>
      </Modal.Footer>
    </Form>
  </Modal>;
}
